import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { authFetchWithRefresh, __resetAuthFetchStateForTests } from './auth-fetch'
import {
  HISTORY_ENGINE_HEADER,
  isHistoryUrl,
  parseServerTiming,
  readDevHistoryEngine,
  recordHistoryTiming,
  type HistoryEngine,
} from './history-engine'

/**
 * Token type per API:
 * - edgeUsersApi    → accessToken
 * - edgeDataApi     → accessToken
 * - edgeResearchApi → accessToken
 */
type TokenType = 'access' | 'id'

/**
 * Creates an RTK Query base query with automatic token injection and
 * 401 retry with token refresh (coalesced via shared mutex).
 *
 * The auth-refresh logic lives in `auth-fetch.ts` so non-RTKQ callers
 * (workspace-api, layout-api) can share the same behavior. This wrapper
 * just plumbs `authFetchWithRefresh` into `fetchBaseQuery`'s `fetchFn` slot.
 */
export function createAuthBaseQuery(
  baseUrl: string,
  tokenType: TokenType,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  return fetchBaseQuery({
    baseUrl,
    fetchFn: async (input, init) => {
      // The developer History engine switch (history-engine.ts): the header on History
      // requests, and each answer's timing kept for the debug sidebar.
      const url = input instanceof Request ? input.url : input.toString()
      const engine = isHistoryUrl(url) ? readDevHistoryEngine() : null
      const started = performance.now()
      const res = await send(input, init, engine)
      if (engine) recordHistoryTiming(timingOf(url, engine, res, performance.now() - started))
      return res
    },
  })

  async function send(
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    engine: HistoryEngine | null,
  ): Promise<Response> {
    // RTK Query passes either a Request object or a string URL. Normalize
    // to the (url, init) shape that authFetchWithRefresh expects.
    if (input instanceof Request) {
      const headers = new Headers(input.headers)
      // Authorization header is added by authFetchWithRefresh; strip any
      // pre-set one to avoid double-injection if Request happens to carry it.
      headers.delete('Authorization')
      if (engine) headers.set(HISTORY_ENGINE_HEADER, engine)
      // `input.body` is a ReadableStream — forwarding it to a fresh fetch
      // requires `duplex: 'half'` (Chrome rejects streaming bodies without
      // it). We don't need streaming, so read the body to a string. Tested
      // by `base-query.unit.test.ts` "reads Request body to a string before
      // re-issuing".
      const hasBody = input.method !== 'GET' && input.method !== 'HEAD'
      const body =
        (init?.body as BodyInit | null | undefined) ??
        (hasBody ? await input.clone().text() : undefined)
      return authFetchWithRefresh(input.url, {
        ...init,
        method: input.method,
        headers,
        body,
        tokenType,
      })
    }
    if (!engine) return authFetchWithRefresh(input.toString(), { ...init, tokenType })
    const headers = new Headers(init?.headers)
    headers.set(HISTORY_ENGINE_HEADER, engine)
    return authFetchWithRefresh(input.toString(), { ...init, headers, tokenType })
  }
}

function timingOf(url: string, asked: HistoryEngine, res: Response, totalMs: number) {
  const from = url.indexOf('/v2/')
  return {
    at: Date.now(),
    path: (from >= 0 ? url.slice(from) : url).split('?')[0],
    asked,
    ...parseServerTiming(res.headers.get('Server-Timing')),
    totalMs,
    status: res.status,
  }
}

/**
 * Test-only: clear the in-flight refresh slot so each test starts from a
 * clean state. Delegates to the shared auth-fetch reset.
 */
export function __resetAuthBaseQueryStateForTests(): void {
  __resetAuthFetchStateForTests()
}

/**
 * Unauthenticated base query for public endpoints (e.g., ticker search).
 */
export function createPublicBaseQuery(
  baseUrl: string,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  return fetchBaseQuery({ baseUrl })
}
